import { useEffect, useMemo, useState } from "react";
import { CaretDown, CaretRight, MagnifyingGlass, Warning } from "@phosphor-icons/react";
import { FileTypeIcon, FolderTypeIcon } from "../../file-icons";
import { useWorkspaceChangesModel, type WorkspaceChangesModel } from "./react.tsx";
import { CodeDiffEditor, type CodeDiffEditorMode } from "../../tabs/code-diff-editor.tsx";
import {
  buildChangeTree,
  filterChanges,
  type ChangeTreeNode,
  type WorkspaceChange
} from "./model.ts";

export interface WorkspaceChangesProps {
  readonly workspaceId: string | null;
  /** Current in-editor files; the right-hand side of every diff. */
  readonly files: Readonly<Record<string, string>>;
  /** Bumped by the shell when a turn may have changed files. */
  readonly refreshToken?: number;
  readonly onOpenFile?: (path: string) => void;
  /** Reports the changed-file count so the tab strip can label itself. */
  readonly onCountChange?: (count: number | null) => void;
}

const KIND_BADGE: Record<WorkspaceChange["kind"], string> = {
  added: "A",
  modified: "M",
  deleted: "D"
};

const KIND_CLASS: Record<WorkspaceChange["kind"], string> = {
  added: "text-emerald-500",
  modified: "text-amber-500",
  deleted: "text-red-500"
};

function TreeRows({
  nodes,
  depth,
  selected,
  collapsed,
  onToggle,
  onSelect
}: {
  nodes: readonly ChangeTreeNode[];
  depth: number;
  selected: string | null;
  collapsed: ReadonlySet<string>;
  onToggle: (path: string) => void;
  onSelect: (change: WorkspaceChange) => void;
}) {
  return (
    <>
      {nodes.map((node) => {
        if (node.change) {
          const change = node.change;
          return (
            <button
              key={node.path}
              type="button"
              onClick={() => onSelect(change)}
              style={{ paddingLeft: `${depth * 12 + 8}px` }}
              data-component="changes-row"
              aria-selected={selected === node.path}
            >
              <FileTypeIcon path={node.path} className="h-[14px] w-[14px] shrink-0" />
              <span className="truncate">{node.name}</span>
              <span aria-label={change.kind} data-slot="kind" data-kind={change.kind}>
                {KIND_BADGE[change.kind]}
              </span>
            </button>
          );
        }
        const isCollapsed = collapsed.has(node.path);
        return (
          <div key={node.path}>
            <button
              type="button"
              aria-expanded={!isCollapsed}
              onClick={() => onToggle(node.path)}
              style={{ paddingLeft: `${depth * 12 + 4}px` }}
              className="flex w-full items-center gap-1 py-1 pr-2 text-left text-[12px] text-kumo-subtle hover:text-kumo-default"
            >
              {isCollapsed ? <CaretRight size={11} /> : <CaretDown size={11} />}
              <FolderTypeIcon open={!isCollapsed} className="h-[14px] w-[14px] shrink-0" />
              <span className="truncate">{node.name}</span>
            </button>
            {isCollapsed ? null : (
              <TreeRows
                nodes={node.children}
                depth={depth + 1}
                selected={selected}
                collapsed={collapsed}
                onToggle={onToggle}
                onSelect={onSelect}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

export function WorkspaceChanges(props: WorkspaceChangesProps) {
  const model = useWorkspaceChangesModel({ workspaceId: props.workspaceId, refreshToken: props.refreshToken ?? 0 }, props.files);
  return <WorkspaceChangesView {...props} model={model} />;
}

export function WorkspaceChangesView({
  workspaceId,
  files,
  onOpenFile,
  onCountChange,
  model
}: WorkspaceChangesProps & { readonly model: WorkspaceChangesModel }) {
  const { loaded, summary } = model;
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [mode, setMode] = useState<CodeDiffEditorMode>("unified");


  useEffect(() => setSelected(null), [workspaceId]);

  const baseline = loaded.baseline?.files ?? null;
  const revision = loaded.baseline?.revision ?? null;
  const error = loaded.error;

  // Reported through a ref-free effect so the parent re-renders once per real
  // change, not on every render of this pane.
  useEffect(() => {
    onCountChange?.(summary ? summary.changes.length : null);
  }, [summary, onCountChange]);

  const visible = useMemo(
    () => (summary ? filterChanges(summary.changes, filter) : []),
    [summary, filter]
  );
  const tree = useMemo(() => buildChangeTree(visible), [visible]);

  const selectedChange = summary?.changes.find(
    (change) => change.path === selected
  );

  if (!workspaceId) {
    return (
      <p data-component="changes-empty">No workspace is open.</p>
    );
  }

  return (
    <div className="flex h-full min-h-0">
      <div data-component="changes-sidebar">
        <div data-component="changes-header">
          <span
            data-slot="baseline"
            title={
              revision
                ? `Baseline: revision ${revision.id} — ${revision.summary}`
                : "No saved revision yet"
            }
          >
            {revision ? `Since revision ${revision.id}` : "No baseline yet"}
          </span>
          {summary ? (
            <span data-slot="stat">
              <span className="text-emerald-500">+{summary.added}</span>{" "}
              <span className="text-red-500">−{summary.removed}</span>
            </span>
          ) : null}
        </div>
        <div data-component="changes-filter">
          <MagnifyingGlass size={13} aria-hidden="true" data-slot="icon" />
          <input
            type="search"
            aria-label="Filter changed files"
            placeholder="Filter files"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            data-component="shell-input"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-auto pb-2">
          {error ? (
            <p
              role="alert"
              className="flex items-start gap-1.5 px-3 py-2 text-[12px] text-red-500"
            >
              <Warning size={13} className="mt-0.5 shrink-0" />
              {error}
            </p>
          ) : summary === null ? (
            <p className="px-3 py-2 text-[12px] text-kumo-subtle">
              Loading changes…
            </p>
          ) : visible.length === 0 ? (
            <p className="px-3 py-2 text-[12px] text-kumo-subtle">
              {filter.trim()
                ? "No changed files match the filter."
                : "No changes since the baseline revision."}
            </p>
          ) : (
            <TreeRows
              nodes={tree}
              depth={0}
              selected={selected}
              collapsed={collapsed}
              onToggle={(path) =>
                setCollapsed((current) => {
                  const next = new Set(current);
                  if (next.has(path)) next.delete(path);
                  else next.add(path);
                  return next;
                })
              }
              onSelect={(change) => {
                setSelected(change.path);
                onOpenFile?.(change.path);
              }}
            />
          )}
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        {selectedChange ? (
          <>
            <div data-component="diff-header">
              <span className={`font-mono text-[11px] ${KIND_CLASS[selectedChange.kind]}`}>
                {KIND_BADGE[selectedChange.kind]}
              </span>
              <span className="truncate text-[12px] text-kumo-default">
                {selectedChange.path}
              </span>
              <span className="ml-auto shrink-0 font-mono text-[11px]">
                <span className="text-emerald-500">+{selectedChange.added}</span>{" "}
                <span className="text-red-500">−{selectedChange.removed}</span>
              </span>
              <button
                type="button"
                onClick={() =>
                  setMode((current) =>
                    current === "unified" ? "side-by-side" : "unified"
                  )
                }
                className="shrink-0 rounded border border-kumo-line px-1.5 py-0.5 text-[11px] text-kumo-subtle hover:text-kumo-default"
              >
                {mode === "unified" ? "Unified" : "Side by side"}
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <CodeDiffEditor
                candidateId={`changes:${workspaceId}:${revision?.id ?? "none"}`}
                original={{
                  treeId: `revision-${revision?.id ?? "none"}`,
                  workspaceId,
                  path: selectedChange.path,
                  contents: baseline && Object.hasOwn(baseline, selectedChange.path) ? baseline[selectedChange.path] ?? "" : ""
                }}
                modified={{
                  treeId: "working",
                  workspaceId,
                  path: selectedChange.path,
                  contents: Object.hasOwn(files, selectedChange.path) ? files[selectedChange.path] ?? "" : ""
                }}
                mode={mode}
                active
              />
            </div>
          </>
        ) : (
          <p data-component="changes-empty">
            Select a changed file to see its diff.
          </p>
        )}
      </div>
    </div>
  );
}
